import { CheckOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import { TablePaginationConfig } from 'antd/lib/table';
import React from 'react';
import { defaultTablePagination } from '../../../config/constants';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { translations } from '../../../config/translations';
import { ActionType, ModuleName } from '../../../core/models/enum';
import { authorizeAction } from '../../../helpers/CheckPermissionHelper';
import {
    getKeyFilter,
    getDefaultFilter,
    getRadioFilter,
    getSearchFilter,
} from '../../../helpers/FilterHelper';
import { showError, showConfirm, showSuccess } from '../../../helpers/NotificationHelper';
import { getBooleanComparer, getDefaultComparer } from '../../../helpers/SortHelper';
import { getTableLocale } from '../../../helpers/TableHelper';
import { UserVm, UsersClient, ApiException } from '../../../utils/api';
import { Props } from './index';
import { applySearchParams, getFilterParamsString, getFiltersParams, getPagingSortingParams } from '../../../helpers/SearchParamsHelper';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';

interface State {
    users?: UserVm[];
    loading?: boolean;
    allowEdit: boolean;
    allowDelete: boolean;
}

class UserTable extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            allowEdit: authorizeAction(props.userProfile, ModuleName.Users, ActionType.Update),
            allowDelete: authorizeAction(props.userProfile, ModuleName.Users, ActionType.Delete),
        };
    }

    public componentDidMount = () => {
        this.getUsers();
    };

    public componentDidUpdate(prevProps: Props) {
        const {
            location: { search },
        } = this.props;

        if (prevProps.location.search !== search) {
            this.getUsers();
        }
    }

    private handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null> = {},
        sorter: SorterResult<UserVm> | SorterResult<UserVm>[] = {}
    ) => {
        const {
            history,
            location: { search },
        } = this.props;

        const currSearchParams = new URLSearchParams(search);
        const tableSearchParams = this.createTableSearchParams(pagination, filters, sorter);
        const newSearchParams = applySearchParams(currSearchParams, tableSearchParams);

        history.push({
            pathname: history.location.pathname,
            search: newSearchParams.toString(),
        });
    };

    private createTableSearchParams = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null> = {},
        sorter: SorterResult<UserVm> | SorterResult<UserVm>[] = {}
    ): URLSearchParams => {
        const filterParamsString = getFilterParamsString(filters);
        const singleSorter = sorter as SorterResult<UserVm>;
        const tableSearchParams = new URLSearchParams(filterParamsString);

        tableSearchParams.append('pageIndex', pagination.current?.toString() || '1');
        tableSearchParams.append(
            'pageSize',
            pagination.pageSize?.toString() || pagination.defaultPageSize?.toString() || ''
        );
        tableSearchParams.append('sortBy', singleSorter.field?.toString() && singleSorter.order ? singleSorter.field?.toString() : '');
        tableSearchParams.append('sortOrder', singleSorter.order || '');

        return tableSearchParams;
    };

    private getUsers = async () => {
        this.setState({
            loading: true,
        });

        this.setState({
            users: await new UsersClient().getAll(),
            loading: false,
        });
    };

    private handleDelete = async (id: string) => {
        try {
            await new UsersClient().delete(id);

            showSuccess(translations.users.deletedMessage);
            this.getUsers();
        } catch (error) {
            if (error instanceof ApiException) {
                showError(error.response);
            } else {
                showError(translations.general.errorDeletingData);
            }
        }
    };

    public render(): React.ReactElement {
        const { users, loading, allowDelete, allowEdit } = this.state;
        const { history, location } = this.props;
        const params = new URLSearchParams(location.search);
        const { sortByParam, sortOrderParam, pageIndexParam, pageSizeParam } = getPagingSortingParams(params);
        const filters = getFiltersParams(params);
        const pagination: TablePaginationConfig = {
            ...defaultTablePagination,
            current:
                pageIndexParam && parseInt(pageIndexParam)
                    ? parseInt(pageIndexParam)
                    : defaultTablePagination.defaultCurrent,
            pageSize:
                pageSizeParam && parseInt(pageSizeParam)
                    ? parseInt(pageSizeParam)
                    : defaultTablePagination.defaultPageSize,
        };

        const columns = [
            {
                title: translations.users.name,
                dataIndex: 'name',
                ...getSearchFilter(),
                onFilter: getDefaultFilter('name'),
                sorter: getDefaultComparer('name'),
                filteredValue: filters.name ? [filters.name] : null,
                sortOrder: sortByParam === 'name' ? sortOrderParam : null,
            },
            {
                title: translations.users.surname,
                dataIndex: 'surname',
                ...getSearchFilter(),
                onFilter: getDefaultFilter('surname'),
                sorter: getDefaultComparer('surname'),
                filteredValue: filters.surname ? [filters.surname] : null,
                sortOrder: sortByParam === 'surname' ? sortOrderParam : null,
            },
            {
                title: `${translations.users.email}`,
                dataIndex: 'email',
                ...getSearchFilter(),
                onFilter: getDefaultFilter('email'),
                sorter: getDefaultComparer('email'),
                filteredValue: filters.email ? [filters.email] : null,
                sortOrder: sortByParam === 'email' ? sortOrderParam : null,
            },
            {
                title: `${translations.users.role}`,
                dataIndex: 'role',
                ...getSearchFilter(),
                onFilter: getDefaultFilter('role'),
                sorter: getDefaultComparer('role'),
                filteredValue: filters.role ? [filters.role] : null,
                sortOrder: sortByParam === 'role' ? sortOrderParam : null,
            },
            {
                title: `${translations.users.emailConfirmed}`,
                dataIndex: 'emailConfirmed',
                width: 150,
                align: 'center',
                ...getRadioFilter([
                    { id: 'true', name: 'Potvrđen' },
                    { id: 'false', name: 'Nije potvrđen' },
                ]),
                onFilter: getKeyFilter('emailConfirmed'),
                sorter: getBooleanComparer('emailConfirmed'),
                filteredValue: filters.emailConfirmed ? [filters.emailConfirmed] : null,
                sortOrder: sortByParam === 'emailConfirmed' ? sortOrderParam : null,
                render: (value: boolean) =>
                    value ? <CheckOutlined style={{ width: '100%' }} /> : null,
            },
            {
                title: translations.general.actions,
                key: 'actions',
                width: 100,
                align: 'left',
                className: 'no-print',
                render: (value: UserVm): React.ReactElement => (
                    <>
                        {allowEdit && (
                            <EditOutlined
                                onClick={() =>
                                    history.push(
                                        Routes.ROUTE_USERS_EDIT.replace(
                                            ReplaceStrings.USER_ID,
                                            value.id.toString()
                                        )
                                    )
                                }
                            />
                        )}
                        {allowDelete && (
                            <DeleteOutlined
                                onClick={() =>
                                    showConfirm(
                                        () => this.handleDelete(value.id),
                                        translations.users.deleteConfirmation,
                                        translations.general.delete,
                                        true
                                    )
                                }
                                className="red-5"
                                style={{ marginLeft: 10 }}
                            />
                        )}
                    </>
                ),
            },
        ];

        return (
            <Table
                locale={getTableLocale()}
                columns={columns}
                dataSource={users}
                loading={loading}
                rowKey={(record: UserVm): string => record.id?.toString()}
                pagination={pagination}
                onChange={this.handleTableChange}
                bordered
                size="small"
            />
        );
    }
}

export default UserTable;
